import { zoning_indice_sources } from '@popety-io/types'
import { getEnvelopeVariant } from '@popety-io/utils'
import { formatMetricValue } from '../../utils/zoningHelpers'
import { ZoningLandData } from '../../hooks/useZoningMetrics'
import { MetricData } from './metricTableHelpers'

/** One line of the envelope calculation breakdown shown in the modal. */
export interface EnvelopeCalcRow {
  op?: '−' | '×' | '=' | '·' | '+'
  label: string
  sub?: string
  value: string
  badge?: zoning_indice_sources | '3d'
  tone?: 'result' | 'total' | 'warn' | 'muted' | 'negative'
}

export interface EnvelopeCalcLabels {
  constructibleSurface?: string
  constructibleSurfaceSub?: string
  smallSetback?: string
  smallSetbackSub?: string
  smallSetbackContiguousSub?: string
  contiguousLabel?: string
  contiguousSub?: string
  largeSetback?: string
  largeSetbackUnknownSub?: string
  unknownValue?: string
  buildableFootprint?: string
  capsLabel?: string
  capsNotApplied?: string
  capsAppliedSub?: string
  effectiveFootprint?: string
  totalHeight?: string
  facadeHeight?: string
  roofAllowance?: string
  roofAllowanceSub?: string
  maxVolume?: string
  existingVolume?: string
  existingVolumeSub?: string
  remainingPotential?: string
  aboveEnvelope?: string
}

export const DEFAULT_ENVELOPE_CALC_LABELS: EnvelopeCalcLabels = {
  constructibleSurface: 'Determining land area (DLA)',
  constructibleSurfaceSub: 'plot area within the building zone',
  smallSetback: 'Small boundary setback',
  smallSetbackSub: 'applied to every plot boundary',
  smallSetbackContiguousSub: 'applied to non-attached boundaries only',
  contiguousLabel: 'Contiguous construction (ordre contigu)',
  contiguousSub: 'buildings may be built to the boundary',
  largeSetback: 'Large boundary setback',
  largeSetbackUnknownSub: 'not yet available — the envelope may be optimistic',
  unknownValue: 'unknown',
  buildableFootprint: 'Buildable footprint',
  capsLabel: 'Max building length / depth',
  capsNotApplied: 'not applied',
  capsAppliedSub: 'the cap reduces the usable footprint',
  effectiveFootprint: 'Effective footprint',
  totalHeight: 'Total height (Gesamthöhe)',
  facadeHeight: 'Facade height (Gebäudehöhe)',
  roofAllowance: 'Standard roof allowance',
  roofAllowanceSub: 'approximates the roof volume (Popety default)',
  maxVolume: 'Maximum building volume',
  existingVolume: 'Existing building volume (VBr)',
  existingVolumeSub: 'Swiss 3D building data',
  remainingPotential: 'Remaining potential',
  aboveEnvelope: "Above today's envelope — acquired rights",
}

const findMetric = (
  metrics: MetricData[],
  ...keys: string[]
): MetricData | undefined => {
  for (const key of keys) {
    // Setback metrics are min-typed (minimum distance), heights max-typed —
    // accept either bound.
    const metric = metrics.find(
      (m) => m.key === key && (m.max !== undefined || m.min !== undefined)
    )
    if (metric) return metric
  }
  return undefined
}

/** Effective value of a metric: max when present, else min (setbacks). */
const metricValue = (metric?: MetricData): number | undefined =>
  metric?.max ?? metric?.min

/** Resolves a metric's zone-level default source (REG/GEO/ALG badge). */
export type EnvelopeSourceResolver = (
  key: string,
  bound: 'min' | 'max'
) => string | undefined

const metricSource = (
  metric?: MetricData,
  getDefaultSource?: EnvelopeSourceResolver
): string | undefined => {
  if (!metric) return undefined
  const bound = metric.max !== undefined ? 'max' : 'min'
  const own = bound === 'max' ? metric.maxSource : metric.minSource
  return own ?? getDefaultSource?.(metric.key, bound)
}

const asBadge = (source?: string): zoning_indice_sources | undefined =>
  source ? (source as zoning_indice_sources) : undefined

/**
 * Builds the calculation breakdown for the envelope source modal: the
 * formula as rows, each official input carrying its own source badge.
 * Scenario flags (setback_differentiated, *_capped, envelope_height_used)
 * become literal lines of the formula — see the approved design.
 */
export const buildEnvelopeCalculationRows = (
  landData: ZoningLandData,
  metrics: MetricData[],
  labels?: EnvelopeCalcLabels,
  getDefaultSource?: EnvelopeSourceResolver
): EnvelopeCalcRow[] => {
  const l = { ...DEFAULT_ENVELOPE_CALC_LABELS, ...labels }
  const rows: EnvelopeCalcRow[] = []
  const variant = getEnvelopeVariant(landData.indice_used)

  const smallSetback = findMetric(metrics, 'small_setback', 'distance_limit')
  const bigSetback = findMetric(metrics, 'big_setback')
  const totalHeight = findMetric(metrics, 'building_total_height')
  const facadeHeight = findMetric(metrics, 'building_without_roof_height')

  if (landData.std != null) {
    rows.push({
      label: l.constructibleSurface || '',
      sub: l.constructibleSurfaceSub,
      value: formatMetricValue(landData.std, 'm²'),
    })
  }

  const smallSetbackValue = metricValue(smallSetback)
  if (smallSetbackValue !== undefined) {
    rows.push({
      op: '−',
      label: l.smallSetback || '',
      sub:
        variant === 'contiguous'
          ? l.smallSetbackContiguousSub
          : l.smallSetbackSub,
      value: formatMetricValue(smallSetbackValue, 'm'),
      badge: asBadge(metricSource(smallSetback, getDefaultSource)),
    })
  }

  if (landData.setback_differentiated === false) {
    rows.push({
      op: '−',
      label: l.largeSetback || '',
      sub: l.largeSetbackUnknownSub,
      value: l.unknownValue || '',
      tone: 'warn',
    })
  } else if (
    landData.setback_differentiated === true &&
    metricValue(bigSetback) !== undefined
  ) {
    rows.push({
      op: '−',
      label: l.largeSetback || '',
      value: formatMetricValue(metricValue(bigSetback)!, 'm'),
      badge: asBadge(metricSource(bigSetback, getDefaultSource)),
    })
  }

  if (variant === 'contiguous') {
    rows.push({
      op: '·',
      label: l.contiguousLabel || '',
      sub: l.contiguousSub,
      value: '',
      tone: 'muted',
    })
  }

  const footprintIsZero = landData.buildable_area_setback === 0
  if (landData.buildable_area_setback != null) {
    rows.push({
      op: '=',
      label: l.buildableFootprint || '',
      value: formatMetricValue(landData.buildable_area_setback, 'm²'),
      tone: footprintIsZero ? 'negative' : 'result',
    })
  }

  const capped =
    landData.building_length_capped === true ||
    landData.building_depth_capped === true
  if (!footprintIsZero) {
    if (capped && landData.buildable_area_effective != null) {
      rows.push({
        op: '·',
        label: l.capsLabel || '',
        sub: l.capsAppliedSub,
        value: '',
        tone: 'warn',
      })
      rows.push({
        op: '=',
        label: l.effectiveFootprint || '',
        value: formatMetricValue(landData.buildable_area_effective, 'm²'),
        tone: 'result',
      })
    } else {
      rows.push({
        op: '·',
        label: l.capsLabel || '',
        value: l.capsNotApplied || '',
        tone: 'muted',
      })
    }
  }

  const usesRoofAllowance =
    landData.envelope_height_used === 'without_roof_plus_allowance'
  const heightMetric = usesRoofAllowance
    ? (facadeHeight ?? totalHeight)
    : (totalHeight ?? facadeHeight)
  const heightValue = metricValue(heightMetric)
  if (!footprintIsZero && heightValue !== undefined) {
    rows.push({
      op: '×',
      label: usesRoofAllowance ? l.facadeHeight || '' : l.totalHeight || '',
      value: formatMetricValue(heightValue, 'm'),
      badge: asBadge(metricSource(heightMetric, getDefaultSource)),
    })
    if (usesRoofAllowance) {
      // Pipeline default: half of the 2.5 m ridge allowance (data-team README)
      rows.push({
        op: '+',
        label: l.roofAllowance || '',
        sub: l.roofAllowanceSub,
        value: formatMetricValue(1.25, 'm'),
        badge: 'estimate',
        tone: 'warn',
      })
    }
  }

  // The chain deliberately stops at the authorized value: like every other
  // source modal, this explains the ruling, not the parcel's exploitation —
  // current vs authorized already lives in the table row.
  if (landData.volume_envelope_authorized != null) {
    rows.push({
      op: '=',
      label: l.maxVolume || '',
      value: formatMetricValue(landData.volume_envelope_authorized, 'm³'),
      tone: 'result',
    })
  }

  return rows
}
